import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { MapContainer, GeoJSON, TileLayer } from "react-leaflet";
import { Info } from "lucide-react";
import { Tooltip as InfoTooltip } from "react-tooltip";
import "leaflet/dist/leaflet.css";

const Wrapper = styled.div`
  width: 100%;
  padding: 20px;
`;

const ChartTitle = styled.div`
  font-family: Arial, sans-serif;
  font-size: 12px;
  font-weight: bold;
  color: #666;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const InfoIconWrapper = styled.div`
  cursor: pointer;
  color: #666;
  transition: color 0.3s;
  &:hover { color: #000; }
  svg { width: 18px; height: 18px; }
`;

const Controls = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 20px;
  margin-bottom: 20px;
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Select = styled.select`
  padding: 8px 12px;
  font-size: 15px;
  border: 1px solid #ccc;
  border-radius: 4px;
`;

/* ---------- Legend (aligned like your screenshot) ---------- */
const LegendWrap = styled.div`
  max-width: 800px;
  margin: 24px auto 0;
  padding: 20px;
`;

const LegendTitle = styled.h4`
  text-align: center;
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 18px;
  color: #333;
`;

const LegendGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px 48px;
  align-items: start;
`;

const LegendItem = styled.div`
  display: grid;
  grid-template-columns: 22px auto; /* swatch | text */
  column-gap: 10px;
  align-items: center;
  min-height: 28px;
`;

const Swatch = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 6px;
  border: 1px solid #999;
  box-shadow: 0 1px 3px rgba(0,0,0,0.2);
`;

const LegendTexts = styled.div`
  display: flex;
  flex-direction: column;
  line-height: 1.2;
`;

const LegendLabel = styled.div`
  font-size: 14px;
  font-weight: bold;
  color: #333;
  white-space: nowrap;
`;

const LegendDesc = styled.div`
  font-size: 12px;
  color: #555;
`;

/* ---------- Colors (green good → red bad) ---------- */
const COLOR_VERY_HIGH = "#2e7d32"; // ≥ 50%
const COLOR_HIGH      = "#81c784"; // 25–49%
const COLOR_MEDIUM    = "#ffeb3b"; // 10–24%
const COLOR_LOW       = "#d32f2f"; // 1–9%
const COLOR_NONE      = "#bdbdbd"; // 0%

const ZakupyByPolandRegionChart = ({ purchaseData = [], clientsData = [] }) => {
  const [geoData, setGeoData] = useState(null);
  const [regionPurchaseData, setRegionPurchaseData] = useState([]);
  const [totalPurchase, setTotalPurchase] = useState(0);
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("all");
  const [error, setError] = useState(null);

  const regionNameMapping = {
    MAZOWIECKIE: "mazowieckie",
    ŚLĄSKIE: "śląskie",
    PODKARPACKIE: "podkarpackie",
    LUBUSKIE: "lubuskie",
    ZACHODNIOPOMORSKIE: "zachodniopomorskie",
    WIELKOPOLSKIE: "wielkopolskie",
    DOLNOŚLĄSKIE: "dolnośląskie",
    ŁÓDZKIE: "łódzkie",
    ŚWIĘTOKRZYSKIE: "świętokrzyskie",
    POMORSKIE: "pomorskie",
    LUBELSKIE: "lubelskie",
    OPOLSKIE: "opolskie",
    PODLASKIE: "podlaskie",
    "WARMIŃSKO-MAZURSKIE": "warmińsko-mazurskie",
    "KUJAWSKO-POMORSKIE": "kujawsko-pomorskie",
    MAŁOPOLSKIE: "małopolskie",
  };

  useEffect(() => {
    const fetchGeoData = async () => {
      try {
        const response = await fetch(
          "https://raw.githubusercontent.com/ppatrzyk/polska-geojson/master/wojewodztwa/wojewodztwa-medium.geojson"
        );
        const data = await response.json();
        setGeoData(data);
      } catch {
        setError("Błąd ładowania danych mapy Polski.");
      }
    };
    fetchGeoData();
  }, []);

  useEffect(() => {
    if (purchaseData.length === 0) return;
    const years = [...new Set(purchaseData.map((row) => row.Rok.toString()))].sort();
    setAvailableYears(years);
    setSelectedYear((prev) => prev || years[years.length - 1]);
  }, [purchaseData]);

  useEffect(() => {
    if (!selectedYear || purchaseData.length === 0 || clientsData.length === 0) return;

    const dostawcaToRegion = {};
    clientsData.forEach(client => {
      const nip = client.nip?.trim();
      const regionRaw = client.wojewodztwo?.trim().toUpperCase();
      if (nip) dostawcaToRegion[nip] = regionNameMapping[regionRaw] || "Unknown";
    });

    const filtered = purchaseData.filter(
      row =>
        row.Rok.toString() === selectedYear &&
        (selectedMonth === "all" || parseInt(row.Miesiac) === parseInt(selectedMonth))
    );

    const grouped = filtered.reduce((acc, row) => {
      const region = dostawcaToRegion[row.NrDostawcy?.trim()] || "Unknown";
      const value = (parseFloat(row.K_42) || 0) + (parseFloat(row.K_43) || 0);
      if (value > 0 && region !== "Unknown") {
        acc[region] = (acc[region] || 0) + value;
      }
      return acc;
    }, {});

    const total = Object.values(grouped).reduce((sum, val) => sum + val, 0);
    const formatted = Object.entries(grouped).map(([region, amount]) => ({
      region,
      value: amount,
      percentage: total ? ((amount / total) * 100).toFixed(2) : "0.00",
    }));

    setRegionPurchaseData(formatted);
    setTotalPurchase(total);
  }, [purchaseData, clientsData, selectedYear, selectedMonth]);

  const styleFeature = (feature) => {
    const regionName = feature.properties.nazwa.trim().toLowerCase();
    const data = regionPurchaseData.find((d) => d.region === regionName);
    const pct = data ? parseFloat(data.percentage) : 0;

    const getColor = (p) => {
      if (p >= 50) return COLOR_VERY_HIGH;
      if (p >= 25) return COLOR_HIGH;
      if (p >= 10) return COLOR_MEDIUM;
      if (p > 0)  return COLOR_LOW;
      return COLOR_NONE;
    };

    return {
      fillColor: getColor(pct),
      weight: 0.8,
      opacity: 0.4,
      color: "#555",
      fillOpacity: 0.3,
    };
  };

  const onEachFeature = (feature, layer) => {
    const regionName = feature.properties.nazwa.trim().toLowerCase();
    const data = regionPurchaseData.find((d) => d.region === regionName);
    const value = data ? data.value.toLocaleString("pl-PL") : "Brak danych";
    const pct = data ? `${data.percentage}%` : "0%";

    layer.bindPopup(
      `<strong>${regionName.toUpperCase()}</strong><br />
       <strong>Zakupy:</strong> ${value} zł<br />
       <strong>Udział:</strong> ${pct}`
    );
  };

  const monthNames = [
    "Styczeń", "Luty", "Marzec", "Kwiecień", "Maj", "Czerwiec",
    "Lipiec", "Sierpień", "Wrzesień", "Październik", "Listopad", "Grudzień"
  ];

  return (
    <Wrapper>
      <ChartTitle>
        Udział Zakupów wg Województw
        <InfoIconWrapper data-tooltip-id="zakupy-map-tooltip">
          <Info />
        </InfoIconWrapper>
      </ChartTitle>
      <InfoTooltip
        id="zakupy-map-tooltip"
        place="left"
        content={
          <>
            <strong>Mapa udziału zakupów</strong><br />
            Kolor przedstawia udział województwa w łącznej wartości zakupów.<br />
            Kliknij na region, aby zobaczyć szczegóły.
          </>
        }
      />

      <Controls>
        <FilterGroup>
          <label>Rok:</label>
          <Select
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
          >
            {availableYears.map((year) => (
              <option key={year} value={year}>{year}</option>
            ))}
          </Select>
        </FilterGroup>

        <FilterGroup>
          <label>Miesiąc:</label>
          <Select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
          >
            <option value="all">Cały rok</option>
            {monthNames.map((name, idx) => (
              <option key={idx} value={idx + 1}>{name}</option>
            ))}
          </Select>
        </FilterGroup>
      </Controls>

      {!geoData || regionPurchaseData.length === 0 ? (
        <p style={{ textAlign: "center", marginTop: "20px", color: "#888" }}>
          Brak danych do wyświetlenia.
        </p>
      ) : (
        <>
          <MapContainer
            style={{ height: "500px", width: "100%" }}
            bounds={[[49, 14], [55, 24]]}
            maxBounds={[[49, 14], [55, 24]]}
            zoom={6}
            scrollWheelZoom={false}
            doubleClickZoom={false}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <GeoJSON
              data={geoData}
              style={styleFeature}
              onEachFeature={onEachFeature}
            />
          </MapContainer>

          {/* Aligned legend */}
          <LegendWrap>
            <LegendTitle>Legenda mapy udziałów zakupów</LegendTitle>
            <LegendGrid>
              {[
                { color: COLOR_VERY_HIGH, label: "≥ 50%",     desc: "bardzo wysoki udział (dobry)" },
                { color: COLOR_HIGH,      label: "25% - 49%", desc: "wysoki udział (dobry)" },
                { color: COLOR_MEDIUM,    label: "10% - 24%", desc: "średni udział (neutralny)" },
                { color: COLOR_LOW,       label: "1% - 9%",   desc: "niski udział (zły)" },
                { color: COLOR_NONE,      label: "0%",        desc: "brak udziału" },
              ].map((item, index) => (
                <LegendItem key={index}>
                  <Swatch style={{ backgroundColor: item.color }} />
                  <LegendTexts>
                    <LegendLabel>{item.label}</LegendLabel>
                    <LegendDesc>{item.desc}</LegendDesc>
                  </LegendTexts>
                </LegendItem>
              ))}
            </LegendGrid>
          </LegendWrap>
        </>
      )}
    </Wrapper>
  );
};

export default ZakupyByPolandRegionChart;
